import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  connectorDetailQueryOptions,
  connectorLabel,
  connectorToolsQueryOptions,
  connectorsQueryOptions,
  copyConnector,
  createConnector,
  deleteConnector,
  updateConnector,
  type Connector,
  type ConnectorBody,
  type ConnectorTestResult,
} from "@/modules/chat/queries/connectors-queries";
import {
  personalSpaceQueryOptions,
  spacesListQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { ConnectorIcon } from "./ConnectorIcon";
import { CopyToSpaceDialog } from "./CopyToSpaceDialog";

type Tab = "all" | "personal" | "shared";
type Mode =
  | { kind: "catalogue" }
  | { kind: "new" }
  | { kind: "edit"; id: string };

interface HeaderRow {
  id: string; // stable React key, NOT the header name
  key: string; // header name (e.g. "Authorization")
  value: string; // current value being typed; "" = leave alone
  existing: boolean; // true if this header already has a saved secret
}

interface FormState {
  name: string;
  label: string;
  description: string;
  enabled: boolean;
  url: string;
  headers: HeaderRow[];
  deletedKeys: string[]; // existing keys the user removed; sent as null on save
  /** `null` = personal scope, string = team space id. */
  spaceId: string | null;
  /** Raw (unprefixed) MCP tool names that require user approval before
   *  execution. Persisted in `config.approval_required_tools`. */
  approvalRequired: string[];
  /** JSON text of config keys the form has no dedicated field for
   *  (tls, http2, sber_signing_base_url, …). Round-tripped verbatim so
   *  saving never destroys config this editor doesn't understand. */
  advancedJson: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  label: "",
  description: "",
  enabled: true,
  url: "",
  headers: [],
  deletedKeys: [],
  spaceId: null,
  approvalRequired: [],
  advancedJson: "",
};

/** Config keys owned by dedicated form fields — everything else lives in
 *  the advanced-settings JSON. */
const MANAGED_CONFIG_KEYS = new Set(["url", "header_keys", "approval_required_tools"]);

let HEADER_ROW_COUNTER = 0;
const newHeaderId = () => `h${++HEADER_ROW_COUNTER}`;

export function ConnectorsAdminPage() {
  const { data: connectors = [], isLoading } = useQuery(
    connectorsQueryOptions("all"),
  );
  const [mode, setMode] = useState<Mode>({ kind: "catalogue" });

  if (mode.kind === "new" || mode.kind === "edit") {
    return (
      <ConnectorBuilder
        mode={mode.kind}
        id={mode.kind === "edit" ? mode.id : undefined}
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }

  return (
    <ConnectorsCatalogue
      connectors={connectors}
      isLoading={isLoading}
      onCreate={() => setMode({ kind: "new" })}
      onEdit={(id) => setMode({ kind: "edit", id })}
    />
  );
}

interface CatalogueProps {
  connectors: Connector[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (id: string) => void;
}

function ConnectorsCatalogue({
  connectors,
  isLoading,
  onCreate,
  onEdit,
}: CatalogueProps) {
  const qc = useQueryClient();
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [spaceFilterId, setSpaceFilterId] = useState<string | null>(null);
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false);
  const spaceMenuRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [copyTarget, setCopyTarget] = useState<Connector | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteConnector(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });

  // «Приватный» коннектор = висит в личном пространстве. Сравниваем по id
  // /spaces/personal, иначе свежеcоздaнное командное с одним участником
  // протекает в таб «Приватные» (та же ловушка, что и в HistoryView).
  const isPersonalConnector = (c: Connector) =>
    !c.space || (!!personalSpace && c.space.id === personalSpace.id);

  const filtered = useMemo(() => {
    const scoped = connectors.filter((c) => {
      if (spaceFilterId && c.space?.id !== spaceFilterId) return false;
      if (tab === "personal" && !isPersonalConnector(c)) return false;
      if (tab === "shared" && isPersonalConnector(c)) return false;
      return true;
    });
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.label || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectors, tab, spaceFilterId, search, personalSpace]);

  // If the picked space disappears (lost access), silently fall back to "all".
  useEffect(() => {
    if (spaceFilterId && !spaces.some((s) => s.id === spaceFilterId)) {
      setSpaceFilterId(null);
    }
  }, [spaces, spaceFilterId]);

  useEffect(() => {
    if (!spaceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (spaceMenuRef.current?.contains(e.target as Node)) return;
      setSpaceMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSpaceMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [spaceMenuOpen]);

  const spaceFilterLabel = spaceFilterId
    ? spaces.find((s) => s.id === spaceFilterId)?.name ?? "Все пространства"
    : "Все пространства";

  const sectionTitle =
    tab === "personal"
      ? "Мои коннекторы"
      : tab === "shared"
        ? "Командные коннекторы"
        : "Все коннекторы";

  function handleDelete(c: Connector) {
    if (!confirm(`Удалить коннектор «${connectorLabel(c)}»?`)) return;
    deleteMutation.mutate(c.id);
    setOpenMenu(null);
  }

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Каталог коннекторов</h1>
        <button type="button" className="cmd-page-create" onClick={onCreate}>
          <PlusIcon />
          <span>Добавить коннектор</span>
        </button>
      </header>

      <div className="cmd-page-toolbar">
        <div className="history-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "all"}
            className={`history-tab${tab === "all" ? " active" : ""}`}
            onClick={() => setTab("all")}
          >
            Все
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "personal"}
            className={`history-tab${tab === "personal" ? " active" : ""}`}
            onClick={() => setTab("personal")}
          >
            Приватные
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "shared"}
            className={`history-tab${tab === "shared" ? " active" : ""}`}
            onClick={() => setTab("shared")}
          >
            Командные
          </button>
        </div>
        <div className="history-filters">
          <div className="history-filter-popover-anchor" ref={spaceMenuRef}>
            <button
              type="button"
              className={`history-filter-select${spaceFilterId ? " is-applied" : ""}${spaceMenuOpen ? " is-open" : ""}`}
              onClick={() => setSpaceMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={spaceMenuOpen}
            >
              <span className="history-filter-select-label">{spaceFilterLabel}</span>
              <Chevron />
            </button>
            {spaceMenuOpen && (
              <div className="history-filter-popover" role="menu">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!spaceFilterId}
                  className={`history-filter-popover-item${!spaceFilterId ? " is-active" : ""}`}
                  onClick={() => {
                    setSpaceFilterId(null);
                    setSpaceMenuOpen(false);
                  }}
                >
                  Все пространства
                </button>
                {spaces.length > 0 && <div className="history-filter-popover-sep" />}
                {spaces.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={spaceFilterId === s.id}
                    className={`history-filter-popover-item${spaceFilterId === s.id ? " is-active" : ""}`}
                    onClick={() => {
                      setSpaceFilterId(s.id);
                      setSpaceMenuOpen(false);
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="cmd-search">
            <SearchIcon />
            <input
              type="search"
              className="cmd-search-input"
              placeholder="Название коннектора"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <h2 className="cmd-page-section-title">{sectionTitle}</h2>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : filtered.length === 0 ? (
        <div className="cmd-empty">
          {search ? (
            <p>Ничего не найдено по запросу «{search}»</p>
          ) : tab === "personal" ? (
            <>
              <p className="cmd-empty-title">Приватных коннекторов пока нет</p>
              <p className="cmd-empty-hint">
                Подключите MCP-сервер, чтобы добавить агенту новые тулы.
              </p>
            </>
          ) : tab === "shared" ? (
            <>
              <p className="cmd-empty-title">Командных коннекторов пока нет</p>
              <p className="cmd-empty-hint">
                Добавьте коннектор в одно из своих пространств — он появится здесь.
              </p>
            </>
          ) : spaceFilterId ? (
            <p>В выбранном пространстве пока нет коннекторов.</p>
          ) : (
            <p>Коннекторов пока нет</p>
          )}
        </div>
      ) : (
        <div className="cmd-grid">
          {filtered.map((c) => (
            <ConnectorCard
              key={c.id}
              connector={c}
              scopeBadge={
                isPersonalConnector(c) ? "Личное" : c.space?.name ?? null
              }
              menuOpen={openMenu === c.id}
              onMenuToggle={() =>
                setOpenMenu((m) => (m === c.id ? null : c.id))
              }
              onMenuClose={() => setOpenMenu(null)}
              onEdit={() => onEdit(c.id)}
              onCopy={() => {
                setCopyTarget(c);
                setOpenMenu(null);
              }}
              onDelete={() => handleDelete(c)}
            />
          ))}
        </div>
      )}

      {copyTarget && (
        <CopyToSpaceDialog
          title="Скопировать коннектор"
          sourceLabel={connectorLabel(copyTarget)}
          sourceSpaceId={copyTarget.space?.id ?? null}
          onCopy={(toSpaceId) => copyConnector(copyTarget.id, toSpaceId)}
          onClose={() => setCopyTarget(null)}
          invalidateKeys={[["connectors"]]}
        />
      )}
    </div>
  );
}

interface CardProps {
  connector: Connector;
  /** Where the connector lives, in the same plashka style as AgentCard:
   *  «Личное» for personal scope, the space name for team scope. `null`
   *  hides the badge (e.g. while /spaces/personal is loading). */
  scopeBadge: string | null;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}

function ConnectorCard({
  connector,
  scopeBadge,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onCopy,
  onDelete,
}: CardProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const url = (connector.config?.url as string | undefined) ?? "";
  const host = (() => {
    try {
      return new URL(url).host;
    } catch {
      return url;
    }
  })();

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onMenuClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMenuClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuClose]);

  return (
    <div className="cmd-card">
      <div className="cmd-card-head">
        <div className="cmd-card-head-main">
          <ConnectorIcon
            url={url}
            label={connectorLabel(connector)}
            size={28}
          />
          <span className="cmd-card-name">{connectorLabel(connector)}</span>
          {scopeBadge && (
            <span className="cmd-card-badge">{scopeBadge}</span>
          )}
          {!connector.enabled && (
            <span className="cmd-card-badge">Выключен</span>
          )}
        </div>
        <div className="cmd-card-menu-wrap" ref={wrapRef}>
          <button
            type="button"
            className="cmd-card-kebab"
            onClick={onMenuToggle}
            aria-label="Действия"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <KebabIcon />
          </button>
          {menuOpen && (
            <div className="cmd-card-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="cmd-card-menu-item"
                onClick={() => {
                  onMenuClose();
                  onEdit();
                }}
              >
                <EditIcon />
                <span>Редактировать</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="cmd-card-menu-item"
                onClick={onCopy}
              >
                <CopyIcon />
                <span>Скопировать</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="cmd-card-menu-item cmd-card-menu-item--danger"
                onClick={() => {
                  onMenuClose();
                  onDelete();
                }}
              >
                <TrashIcon />
                <span>Удалить</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="cmd-card-desc">
        {connector.description || host || "Без описания"}
      </p>
    </div>
  );
}

// ── Builder ──────────────────────────────────────────────────────────

/** Parse the advanced-settings JSON into extra config keys.
 *  Returns null on malformed JSON / non-object. Managed keys are dropped —
 *  the dedicated form fields own them. */
function parseAdvancedConfig(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (!MANAGED_CONFIG_KEYS.has(k)) out[k] = v;
  }
  return out;
}

function bodyFromForm(form: FormState): ConnectorBody {
  // Drop blank-key rows so an empty "+ Добавить" placeholder doesn't pollute
  // header_keys. Two rows with the same key — keep both keys in the list
  // (the backend dedupes), but only the LAST non-empty value wins.
  const liveRows = form.headers.filter((h) => h.key.trim());
  const config: Record<string, unknown> = {
    ...(parseAdvancedConfig(form.advancedJson) ?? {}),
    url: form.url.trim(),
    header_keys: Array.from(new Set(liveRows.map((h) => h.key.trim()))),
    approval_required_tools: Array.from(new Set(form.approvalRequired)),
  };

  // Build secrets payload:
  //   - new value (non-empty) → replace
  //   - empty + existing      → "" sentinel = "leave the saved value alone"
  //   - empty + new           → skip entirely (no point saving an empty secret)
  //   - row was deleted       → null = remove the saved secret
  const secrets: Record<string, string | null> = {};
  for (const h of liveRows) {
    const k = h.key.trim();
    const v = h.value;
    if (v) {
      secrets[k] = v;
    } else if (h.existing) {
      secrets[k] = "";
    }
  }
  for (const k of form.deletedKeys) {
    if (!secrets[k]) secrets[k] = null;
  }

  return {
    name: form.name.trim().toLowerCase(),
    label: form.label,
    description: form.description,
    config,
    enabled: form.enabled,
    secrets,
    space_id: form.spaceId,
  };
}

interface BuilderProps {
  mode: "new" | "edit";
  id?: string;
  onClose: () => void;
}

function ConnectorBuilder({ mode, id, onClose }: BuilderProps) {
  const qc = useQueryClient();
  const isNew = mode === "new";
  const { data, isLoading } = useQuery({
    ...connectorDetailQueryOptions(id ?? ""),
    enabled: !!id,
  });
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const activeSpaceId = useSpaceStore((s) => s.currentSpaceId);

  // Auto-fetch the MCP tool list as soon as an existing connector is opened.
  // Cached for 5 min so re-opening the editor doesn't re-spawn the session.
  const {
    data: tools,
    isFetching: toolsLoading,
    refetch: refetchTools,
  } = useQuery({
    ...connectorToolsQueryOptions(id ?? ""),
    enabled: !!id,
  });

  // Seed the new-form scope from the sidebar's active space — the space the
  // user was in when they hit "Добавить" is almost always where they want
  // the connector to live. Personal is the implicit default scope, and
  // `spacesListQueryOptions()` filters it out at the SQL level, so any
  // active id that isn't in `spaces` falls back to personal.
  const seedSpaceId = useMemo(() => {
    if (!activeSpaceId) return null;
    return spaces.some((s) => s.id === activeSpaceId) ? activeSpaceId : null;
  }, [activeSpaceId, spaces]);

  const [form, setForm] = useState<FormState>(() =>
    isNew ? { ...EMPTY_FORM, spaceId: seedSpaceId } : EMPTY_FORM,
  );
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState<string | null>(null);

  // Re-seed scope when spaces finish loading after the dialog opened.
  // Only applies to "new" mode and only before the user has interacted
  // with the select — picking "Личное" sets spaceId to null, which must
  // not be treated as "still unpicked" and snapped back to the seed.
  const userPickedSpaceRef = useRef(false);
  useEffect(() => {
    if (!isNew) return;
    if (userPickedSpaceRef.current) return;
    if (form.spaceId !== null) return;
    if (seedSpaceId !== null) {
      setForm((f) => ({ ...f, spaceId: seedSpaceId }));
    }
  }, [isNew, seedSpaceId, form.spaceId]);

  useEffect(() => {
    if (!loaded && data) {
      const cfg = data.config || {};
      const declared = Array.isArray(cfg.header_keys)
        ? (cfg.header_keys as string[])
        : [];
      const stored = new Set(data.secret_keys ?? []);
      const headers: HeaderRow[] = declared.map((k) => ({
        id: newHeaderId(),
        key: k,
        value: "",
        existing: stored.has(k),
      }));
      const approval = Array.isArray(cfg.approval_required_tools)
        ? (cfg.approval_required_tools as unknown[]).filter(
            (s): s is string => typeof s === "string" && s.length > 0,
          )
        : [];
      const extra = Object.fromEntries(
        Object.entries(cfg).filter(([k]) => !MANAGED_CONFIG_KEYS.has(k)),
      );
      setForm({
        name: data.name,
        label: data.label,
        description: data.description,
        enabled: data.enabled,
        url: typeof cfg.url === "string" ? cfg.url : "",
        headers,
        deletedKeys: [],
        spaceId: data.space_id ?? null,
        approvalRequired: approval,
        advancedJson: Object.keys(extra).length
          ? JSON.stringify(extra, null, 2)
          : "",
      });
      setLoaded(true);
    }
  }, [loaded, data]);

  const saveMut = useMutation({
    mutationFn: async (body: ConnectorBody): Promise<Connector> => {
      if (isNew) return createConnector(body);
      return updateConnector(id!, body);
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["connectors"] });
      // Config (URL/headers/secrets) may have changed — drop the cached
      // tool list so it gets re-fetched against the new server config.
      if (saved?.id) {
        qc.invalidateQueries({ queryKey: ["connectors", saved.id, "tools"] });
      }
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? humanize(e.message) : "Ошибка сохранения");
    },
  });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  }

  function handleSave() {
    setError(null);
    const slug = form.name.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(slug)) {
      setError("Имя: 1–64 символа, латиница, цифры, дефисы, подчёркивания.");
      return;
    }
    if (!form.url.trim()) {
      setError("Укажите URL MCP-сервера.");
      return;
    }
    if (parseAdvancedConfig(form.advancedJson) === null) {
      setError("Расширенные настройки: невалидный JSON (нужен объект).");
      return;
    }
    const body = bodyFromForm({ ...form, name: slug });
    saveMut.mutate(body);
  }

  const title = isNew
    ? "Новый коннектор"
    : data
      ? connectorLabel(data)
      : (id ?? "Коннектор");
  const saving = saveMut.isPending;
  const advancedJsonInvalid = parseAdvancedConfig(form.advancedJson) === null;
  const canSave =
    form.name.trim().length > 0 &&
    form.url.trim().length > 0 &&
    !advancedJsonInvalid &&
    !saving;

  return (
    <div className="cmd-builder">
      <header className="cmd-builder-topbar">
        <button
          type="button"
          className="cmd-builder-back"
          onClick={onClose}
          aria-label="Назад"
        >
          <ArrowLeftIcon />
        </button>
        <span className="cmd-builder-topbar-icon">
          <ConnectorIcon
            url={form.url || (data?.config?.url as string | undefined) || null}
            label={
              form.label ||
              form.name ||
              (data ? connectorLabel(data) : "?")
            }
            size={24}
          />
        </span>
        <span className="cmd-builder-topbar-title">{title}</span>
      </header>

      <div className="cmd-builder-body">
        <p className="cmd-builder-intro">
          Коннектор подключает удалённый MCP-сервер — его инструменты добавляются агенту.
        </p>

        {error && <div className="cmd-builder-error">{error}</div>}

        <div className="cmd-builder-fields">
          <Field
            label="Имя (slug)"
            hint={
              <>
                Используется как префикс к именам тул:{" "}
                <code>{form.name || "имя"}_search</code>
              </>
            }
          >
            <input
              type="text"
              className="cmd-builder-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="context7"
              autoComplete="off"
              readOnly={!isNew}
            />
          </Field>

          <Field label="Название">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.label}
              onChange={(e) => update("label", e.target.value)}
              placeholder="Context7"
              maxLength={120}
            />
          </Field>

          <Field label="Описание">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Что делает этот коннектор"
              maxLength={240}
            />
          </Field>

          <Field
            label="Пространство"
            hint={
              form.spaceId
                ? "Коннектор будет доступен всем агентам в этом пространстве."
                : "Личный коннектор — виден только в ваших агентах."
            }
          >
            <select
              className="cmd-builder-input"
              value={form.spaceId ?? ""}
              onChange={(e) => {
                userPickedSpaceRef.current = true;
                update("spaceId", e.target.value === "" ? null : e.target.value);
              }}
            >
              <option value="">Личное</option>
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="URL MCP-сервера"
            hint="Полный URL эндпоинта MCP-сервера"
          >
            <input
              type="text"
              className="cmd-builder-input"
              value={form.url}
              onChange={(e) => update("url", e.target.value)}
              placeholder="https://mcp.context7.com/mcp"
            />
          </Field>

          <HeadersField
            headers={form.headers}
            onAdd={() =>
              update("headers", [
                ...form.headers,
                { id: newHeaderId(), key: "", value: "", existing: false },
              ])
            }
            onChangeKey={(idx, key) => {
              const next = [...form.headers];
              const row = next[idx];
              if (!row) return;
              next[idx] = { ...row, key };
              update("headers", next);
            }}
            onChangeValue={(idx, value) => {
              const next = [...form.headers];
              const row = next[idx];
              if (!row) return;
              next[idx] = { ...row, value };
              update("headers", next);
            }}
            onRemove={(idx) => {
              const h = form.headers[idx];
              if (!h) return;
              const next = form.headers.filter((_, i) => i !== idx);
              const nextDeleted =
                h.existing && h.key.trim()
                  ? [...form.deletedKeys, h.key.trim()]
                  : form.deletedKeys;
              setForm({ ...form, headers: next, deletedKeys: nextDeleted });
              setError(null);
            }}
          />

          <AdvancedConfigField
            value={form.advancedJson}
            invalid={advancedJsonInvalid}
            onChange={(v) => update("advancedJson", v)}
          />

          <label className="cmd-builder-checkbox">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => update("enabled", e.target.checked)}
            />
            <span>Включён</span>
          </label>
        </div>

        {!isNew && (
          <ToolsBlock
            tools={tools}
            loading={toolsLoading}
            onRefresh={() => refetchTools()}
            approvalRequired={form.approvalRequired}
            onToggleApproval={(rawName) =>
              setForm((f) => {
                const next = new Set(f.approvalRequired);
                if (next.has(rawName)) next.delete(rawName);
                else next.add(rawName);
                return { ...f, approvalRequired: Array.from(next) };
              })
            }
          />
        )}

        <div className="cmd-builder-actions">
          <button
            type="button"
            className="cmd-page-create"
            onClick={handleSave}
            disabled={!canSave || isLoading}
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
          <button
            type="button"
            className="cmd-builder-cancel"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="cmd-builder-field">
      <span className="cmd-builder-field-label">{label}</span>
      {children}
      {hint && <span className="cmd-builder-field-hint">{hint}</span>}
    </label>
  );
}

interface HeadersFieldProps {
  headers: HeaderRow[];
  onAdd: () => void;
  onChangeKey: (idx: number, key: string) => void;
  onChangeValue: (idx: number, value: string) => void;
  onRemove: (idx: number) => void;
}

function HeadersField({
  headers,
  onAdd,
  onChangeKey,
  onChangeValue,
  onRemove,
}: HeadersFieldProps) {
  return (
    <div className="cmd-builder-field">
      <div className="cmd-builder-field-row">
        <span className="cmd-builder-field-label">HTTP-заголовки</span>
        <button
          type="button"
          className="cmd-builder-field-action"
          onClick={onAdd}
        >
          + Добавить
        </button>
      </div>
      <span className="cmd-builder-field-hint">
        Например: <code>Authorization</code> со значением{" "}
        <code>Bearer ваш_токен</code>. Значения зашифровываются на бэке.
      </span>
      {headers.length === 0 ? (
        <span className="cmd-builder-field-hint">
          Заголовков нет — сервер открыт без авторизации.
        </span>
      ) : (
        <div className="cmd-builder-headers">
          {headers.map((h, idx) => (
            <HeaderRowEdit
              key={h.id}
              row={h}
              onChangeKey={(key) => onChangeKey(idx, key)}
              onChangeValue={(value) => onChangeValue(idx, value)}
              onRemove={() => onRemove(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdvancedConfigField({
  value,
  invalid,
  onChange,
}: {
  value: string;
  invalid: boolean;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Auto-expand once when an existing connector loads with advanced keys —
  // hidden non-empty settings are exactly how config got lost before.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpened.current && value.trim()) {
      autoOpened.current = true;
      setOpen(true);
    }
  }, [value]);

  return (
    <div className="cmd-builder-field">
      <div className="cmd-builder-field-row">
        <span className="cmd-builder-field-label">Расширенные настройки</span>
        <button
          type="button"
          className="cmd-builder-field-action"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Скрыть" : value.trim() ? "Показать" : "+ Настроить"}
        </button>
      </div>
      {open && (
        <>
          <span className="cmd-builder-field-hint">
            JSON с дополнительными ключами конфига — например <code>tls</code>,{" "}
            <code>http2</code>, <code>disable_output_schema_validation_tools</code>.
            Специфичные для конкретного сервера ключи тоже сохраняются здесь. URL,
            заголовки и подтверждения настраиваются полями выше.
          </span>
          <textarea
            className="cmd-builder-textarea cmd-builder-header-mono"
            rows={8}
            spellCheck={false}
            value={value}
            placeholder={'{\n  "http2": true,\n  "tls": { "verify": "ca.pem" }\n}'}
            onChange={(e) => onChange(e.target.value)}
          />
          {invalid && (
            <span className="cmd-builder-field-hint cmd-builder-field-hint--error">
              Невалидный JSON — нужен объект <code>{"{...}"}</code>.
            </span>
          )}
        </>
      )}
    </div>
  );
}

function HeaderRowEdit({
  row,
  onChangeKey,
  onChangeValue,
  onRemove,
}: {
  row: HeaderRow;
  onChangeKey: (v: string) => void;
  onChangeValue: (v: string) => void;
  onRemove: () => void;
}) {
  const isAuth = row.key.trim().toLowerCase() === "authorization";
  const valuePlaceholder =
    row.existing && !row.value
      ? "•••• сохранено"
      : isAuth
        ? "Bearer ваш_токен"
        : "значение";
  return (
    <div className="cmd-builder-header-row">
      <input
        className="cmd-builder-input cmd-builder-header-mono"
        value={row.key}
        placeholder="Authorization"
        onChange={(e) => onChangeKey(e.target.value)}
      />
      <input
        className="cmd-builder-input cmd-builder-header-mono"
        type="password"
        autoComplete="off"
        value={row.value}
        placeholder={valuePlaceholder}
        onChange={(e) => onChangeValue(e.target.value)}
      />
      <button
        type="button"
        className="cmd-builder-header-remove"
        onClick={onRemove}
        title="Удалить заголовок"
        aria-label="Удалить заголовок"
      >
        ×
      </button>
    </div>
  );
}

function ToolsBlock({
  tools,
  loading,
  onRefresh,
  approvalRequired,
  onToggleApproval,
}: {
  tools: ConnectorTestResult | undefined;
  loading: boolean;
  onRefresh: () => void;
  approvalRequired: string[];
  onToggleApproval: (rawName: string) => void;
}) {
  const headerCount =
    tools?.ok && Array.isArray(tools.tools) ? tools.tools.length : null;

  return (
    <div className="cmd-builder-tools">
      <div className="cmd-builder-tools-head">
        <span className="cmd-builder-field-label">Доступные инструменты</span>
        {headerCount !== null && (
          <span className="cmd-builder-tools-count">{headerCount}</span>
        )}
        <button
          type="button"
          className="cmd-builder-field-action"
          onClick={onRefresh}
          disabled={loading}
          title="Запросить список тул заново"
        >
          {loading ? "Обновляем…" : "Обновить"}
        </button>
      </div>

      {loading && !tools ? (
        <div className="cmd-builder-field-hint">Запрашиваем сервер…</div>
      ) : !tools ? (
        <div className="cmd-builder-field-hint">Нет данных</div>
      ) : !tools.ok ? (
        <div className="cmd-builder-error">
          Не удалось подключиться: {tools.error || "неизвестная ошибка"}
        </div>
      ) : (tools.tools ?? []).length === 0 ? (
        <div className="cmd-builder-field-hint">
          Сервер не сообщил ни одной тулы.
        </div>
      ) : (
        <div className="cmd-builder-tools-list">
          {(tools.tools ?? []).map((t) => {
            const raw = t.raw_name || t.name;
            return (
              <ToolRow
                key={t.name}
                name={t.name}
                title={t.title}
                description={t.description}
                approvalChecked={approvalRequired.includes(raw)}
                onToggleApproval={() => onToggleApproval(raw)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ToolRow({
  name,
  title,
  description,
  approvalChecked,
  onToggleApproval,
}: {
  name: string;
  title?: string | null;
  description?: string;
  approvalChecked: boolean;
  onToggleApproval: () => void;
}) {
  const [open, setOpen] = useState(false);
  const desc = (description || "").trim();
  // Collapse internal whitespace runs so a wall of formatting doesn't blow
  // the row out vertically; full text is still available on expand.
  const oneLine = desc.replace(/\s+/g, " ");
  const isLong = oneLine.length > 140;
  const preview = isLong ? oneLine.slice(0, 140).trimEnd() + "…" : oneLine;

  return (
    <div className="cmd-builder-tool">
      <div className="cmd-builder-tool-head">
        {title ? (
          <>
            <span className="cmd-builder-tool-title">{title}</span>
            <code className="cmd-builder-tool-name">{name}</code>
          </>
        ) : (
          <code className="cmd-builder-tool-name">{name}</code>
        )}
        {isLong && (
          <button
            type="button"
            className="cmd-builder-tool-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Свернуть" : "Подробнее"}
          </button>
        )}
        <label className="cmd-builder-tool-approval">
          <input
            type="checkbox"
            checked={approvalChecked}
            onChange={onToggleApproval}
          />
          <span>Требует подтверждения</span>
        </label>
      </div>
      {desc && (
        <div className="cmd-builder-tool-desc">{open ? desc : preview}</div>
      )}
    </div>
  );
}

function humanize(msg: string): string {
  const m = msg.match(/"detail":"([^"]+)"/);
  if (m && m[1]) return m[1];
  return msg.slice(0, 200);
}

// ── Icons ────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M14.1666 3.21354C14.1667 2.84774 14.021 2.49697 13.7623 2.23828C13.5038 1.9797 13.1534 1.83411 12.7877 1.83398C12.4221 1.83394 12.0711 1.97918 11.8125 2.23763L10.7116 3.33854L12.6621 5.28906L13.7623 4.1888C14.021 3.9302 14.1666 3.5793 14.1666 3.21354ZM2.91469 11.1374C2.81831 11.2335 2.74583 11.3517 2.7057 11.4818L2.70635 11.4824L1.91599 14.0833L4.51885 13.2943C4.64934 13.2546 4.76861 13.1835 4.86521 13.0872L11.955 5.99609L10.0045 4.04557L2.91469 11.1374ZM15.1666 3.21354C15.1666 3.80518 14.9461 4.37413 14.5508 4.81055L14.4694 4.89583L13.069 6.29557C13.0541 6.31581 13.0385 6.33586 13.0202 6.35417C13.0018 6.37247 12.9818 6.38812 12.9616 6.40299L5.57094 13.7956C5.38501 13.9809 5.16184 14.1242 4.91664 14.2155L4.80987 14.2513L1.90752 15.1315H1.90622C1.76223 15.1747 1.60931 15.1786 1.46351 15.1419C1.31762 15.1052 1.18456 15.0295 1.0781 14.9232C0.971689 14.8169 0.895629 14.6842 0.858696 14.5384C0.821783 14.3926 0.825387 14.2392 0.868462 14.0951L0.869113 14.0931L1.74932 11.1914L1.74997 11.1901C1.83808 10.9029 1.99556 10.6418 2.20831 10.4297L9.59698 3.03841C9.61183 3.01821 9.62818 2.99874 9.64646 2.98047C9.66473 2.9622 9.6842 2.94584 9.7044 2.93099L11.1048 1.5306C11.551 1.08446 12.1567 0.833905 12.7877 0.833984C13.4187 0.834109 14.0239 1.08504 14.47 1.53125C14.9161 1.97749 15.1667 2.58261 15.1666 3.21354Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.33301 0.832031C9.82896 0.832031 10.2833 1.07533 10.6035 1.39551C10.9237 1.71568 11.167 2.17007 11.167 2.66602V3.49902H14C14.2761 3.49902 14.5 3.72288 14.5 3.99902C14.5 4.27517 14.2761 4.49902 14 4.49902H13.167V13.332C13.167 13.828 12.9237 14.2824 12.6035 14.6025C12.2833 14.9227 11.829 15.166 11.333 15.166H4.66699C4.17104 15.166 3.71666 14.9227 3.39648 14.6025C3.07631 14.2824 2.83301 13.828 2.83301 13.332V4.49902H2C1.72386 4.49902 1.5 4.27517 1.5 3.99902C1.5 3.72288 1.72386 3.49902 2 3.49902H4.83301V2.66602C4.83301 2.17007 5.07631 1.71568 5.39648 1.39551C5.71666 1.07533 6.17104 0.832031 6.66699 0.832031H9.33301ZM3.83301 13.332C3.83301 13.5027 3.92369 13.7157 4.10352 13.8955C4.28334 14.0753 4.49627 14.166 4.66699 14.166H11.333C11.5037 14.166 11.7167 14.0753 11.8965 13.8955C12.0763 13.7157 12.167 13.5027 12.167 13.332V4.49902H3.83301V13.332ZM6.66699 6.83203C6.94288 6.83221 7.16682 7.05615 7.16699 7.33203V11.332C7.16699 11.6081 6.94299 11.8319 6.66699 11.832C6.39085 11.832 6.16699 11.6082 6.16699 11.332V7.33203C6.16717 7.05604 6.39096 6.83203 6.66699 6.83203ZM9.33301 6.83203C9.60904 6.83203 9.83283 7.05604 9.83301 7.33203V11.332C9.83301 11.6082 9.60915 11.832 9.33301 11.832C9.05702 11.8319 8.83301 11.6081 8.83301 11.332V7.33203C8.83318 7.05615 9.05712 6.83221 9.33301 6.83203ZM6.66699 1.83203C6.49627 1.83203 6.28334 1.92271 6.10352 2.10254C5.92369 2.28237 5.83301 2.4953 5.83301 2.66602V3.49902H10.167V2.66602C10.167 2.4953 10.0763 2.28237 9.89648 2.10254C9.71666 1.92271 9.50373 1.83203 9.33301 1.83203H6.66699Z" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3 5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Chevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}
